import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import DoctorDashboardHeader from "./DoctorDashboardHeader";
import DoctorDashboardNav from "./DoctorDashboardNav";
import AuthUser from "../../Hooks/authUser";

const DoctorDashboard = () => {
  const { userInfo } = AuthUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const location = useLocation();

  // Update clock every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Get human-readable page name based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes("overview")) return "Dashboard Overview";
    if (path.includes("statistics")) return "Practice Statistics & Analytics";
    if (path.includes("billing")) return "Billing & Subscriptions";
    if (path.includes("faq")) return "FAQ Support & Documentation";
    if (path.includes("editProfile")) return "Edit Profile Settings";
    if (path.includes("changePassword")) return "Change Account Password";
    if (path.includes("doctor-add-new-patient")) return "Add New Patient Profile";
    if (path.includes("patient-edit-profile")) return "Edit Patient Profile";
    if (path.includes("PatientReport")) return "Patient Clinical Report";
    return "Doctor Portal";
  };

  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="flex h-screen bg-[#f8fafc] overflow-hidden font-sans">
      {/* 1. Desktop Sidebar (Sticky left) */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <DoctorDashboardNav />
      </div>

      {/* 2. Mobile Sidebar Drawer (Slide-over overlay) */}
      {sidebarOpen && (
        <div className="relative z-50 lg:hidden" role="dialog" aria-modal="true">
          {/* Backdrop overlay */}
          <div 
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-slate-900/60 transition-opacity duration-300"
          ></div>

          {/* Drawer menu content */}
          <div className="fixed inset-y-0 left-0 flex w-64 max-w-xs transition duration-300 transform">
            <DoctorDashboardNav onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* 3. Main Content Area */}
      <div className="flex-1 flex flex-col overflow-y-auto focus:outline-none">
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-100 min-h-[70px] flex items-center justify-between px-6 py-2 shadow-sm">
          {/* Left section (Hamburger & Title) */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-500 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-50 transition"
            >
              <Icon icon="lucide:menu" className="text-2xl" />
            </button>
            <div>
              <h1 className="font-heading text-lg md:text-xl font-bold text-slate-800">
                {getPageTitle()}
              </h1>
              <p className="hidden md:block text-xs text-slate-400 mt-0.5">
                Practice management panel
              </p>
            </div>
          </div>

          {/* Right section (Time, User status, Dropdown) */}
          <div className="flex items-center gap-6">
            {/* Clock display */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-100 text-xs font-medium text-slate-500">
              <Icon icon="solar:clock-circle-bold" className="text-base text-slate-400" />
              <span>{formattedDate}</span>
            </div>

            {/* Profile Dropdown */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-700">{userInfo?.name}</p>
                <p className="text-xs text-emerald-500 font-medium flex items-center gap-1 justify-end">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Active
                </p>
              </div>

              <div className="p-1 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-100 transition">
                <DoctorDashboardHeader />
              </div>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <main className="flex-1 p-6 md:p-8 bg-slate-50/50">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DoctorDashboard;
